import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

import { cloneDeep } from 'lodash';
import { BehaviorSubject } from 'rxjs';

import { App, Project, Role, User } from '../../models';
import { SNACKBAR_CONSTANTS } from '../../shared/constants';
import { RolesDataService } from '../data-services';
import type { JsonFormsService } from '../json-forms.service';
import { Field } from './models';

@Injectable({
    providedIn: 'root'
})
export class RolesLogicService {

    private _roles: BehaviorSubject<Role[]> = new BehaviorSubject<Role[]>(null);

    roles$ = this._roles.asObservable();

    roles: Role[] = [];
    role: Role = new Role();

    projectRoles: BehaviorSubject<Role[]> = new BehaviorSubject<Role[]>(null);
    rolesLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    rolesSaving: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    userRole: BehaviorSubject<Role> = new BehaviorSubject<Role>(null);

    constructor(
        private _rolesDataService: RolesDataService,
        private _snackBar: MatSnackBar,
    ) {
        const _roles = JSON.parse(sessionStorage.getItem('roles'));

        if (_roles) {
            this.setRoles(_roles);
        }
    }

    init(): void {
        const _roles = JSON.parse(sessionStorage.getItem('roles'));

        if (_roles) {
            this.setRoles(_roles);
        }
    }

    setRoles(roles: Role[]): void {
        sessionStorage.setItem('roles', JSON.stringify(roles));
        this.roles = roles;
        this._roles.next(roles);
    }

    setRole(role: Role): void {
        sessionStorage.setItem('role', JSON.stringify(role));
        this.role = role;
        this.userRole.next(role);
    }

    resetRoles(): void {
        this._roles.next(this.roles);
    }

    addUpdateRole(role: Role): void {
        if (this.roles.find((u) => u.id === role.id)) {
            const index = this.roles.findIndex((u) => u.id === role.id);
            this.roles[index] = role;
        }
        else {
            this.roles.push(role);
        }

        this.resetRoles();
    }

    getAllRoles(app: App): void {
        this.rolesLoading.next(true);

        this._rolesDataService.getAllRoles(app)
            .subscribe({
                next: (response: Role[]) => {
                    this.roles = response;
                    this.setRoles(response);
                },
                error: () => {
                    this.setRoles([]);
                }
            })
            .add(() => {
                this.rolesLoading.next(false);
            });
    }

    getProjectRoles(projectId: string, returnLocal = false): Role[] {
        this.rolesLoading.next(true);
        const roles = this.roles.filter(x => {
            return x.projectId === projectId;
        });

        if (returnLocal) {
            return roles;
        }
        else {
            this._roles.next(roles);
        }
        this.rolesLoading.next(false);
    }

    filterProjectRoles(project: Project): Role[] {
        return this.roles.filter(x => {
            return x.projectId === project.id;
        });
    }

    createRole(app: App, role: Role): void {
        role.isLoading = true;
        this.rolesSaving.next(true);

        this._rolesDataService.createRole(app, role)
            .subscribe({
                next: (value: any) => {
                    this.addUpdateRole(value);
                    this.rolesSaving.next(false);
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS('Role', 'created'), SNACKBAR_CONSTANTS.CLOSE, { duration: 5000 });
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Role', 'creating'), SNACKBAR_CONSTANTS.CLOSE, { duration: 5000 });
                    role.isLoading = false;
                    this.setRoles(this.roles);
                }
            });
    }

    updateRole(app: App, role: Role): void {
        role.isLoading = true;
        this.rolesSaving.next(true);

        this._rolesDataService.updateRole(app, role)
            .subscribe({
                next: (value: any) => {
                    this.addUpdateRole(value);
                    this.rolesSaving.next(false);
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS('Role', 'updated'), SNACKBAR_CONSTANTS.CLOSE, { duration: 5000 });

                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Role', 'updating'), SNACKBAR_CONSTANTS.CLOSE, { duration: 5000 });
                }
            });
    }

    deleteRole(app: App, role: Role): void {
        role.isLoading = true;
        this._rolesDataService.deleteRole(app, role)
            .subscribe({
                next: () => {
                    this.roles = this.roles.filter(x => x.id !== role.id);
                    this.resetRoles();
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS('Role', 'deleted'), SNACKBAR_CONSTANTS.CLOSE, { duration: 5000 });
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Role', 'deleting'), SNACKBAR_CONSTANTS.CLOSE, { duration: 5000 });
                    role.isLoading = false;
                    this.setRoles(this.roles);
                }
            });
    }

    setFieldsForConfigureRole(role: Role, fields: Field[]): void {
        fields = this.addMapFieldPointGridToFields(fields);
        if (!role.fields || role.fields.length == 0) {
            role.fields = [...fields];
            return;
        }

        role.fields = fields.map(field => {
            const existingField = role.fields.find(roleField => roleField.id === field.id);
            if (!existingField) {
                field.show = true;
                field.enable = true;
                return field;
            }

            field.show = existingField.show;
            field.enable = existingField.enable;
            return field;
        });
    }

    getMapFieldPointPermission(role: Role): boolean {
        let enabled = true;

        const rulesObj = role?.rules || {};
        const mapPointRuleObj = rulesObj['map_field_point_grid'];
        if (mapPointRuleObj?.rule) {
            const effect = mapPointRuleObj.rule.effect;
            const schema = mapPointRuleObj.rule.condition?.schema;
            if (effect === 'ENABLE' && schema?.hasOwnProperty('not')) {
                enabled = false;
            }
        }

        return enabled;
    }

    getEffectiveRoleForCurrentProject(currentProject: Project, appUser: User, projects: Project[], roles: Role[], jsonFormsService: Pick<JsonFormsService, 'getFieldRule'>): Role | undefined {
        if (!appUser || !currentProject) {
            return undefined;
        }

        const allRoles: Role[] = roles?.length ? roles : this.roles ?? [];

        if (!currentProject.isSummaryProject) {
            const appUserProject = appUser.projects?.find(userProject =>
                userProject.projectId === currentProject.id || (userProject as any).id === currentProject.id
            );
            if (appUserProject?.roleId) {
                return allRoles.find(role =>
                    role.id === appUserProject.roleId?.toString() ||
                    role.id === (appUserProject.roleId as any)?.id ||
                    role.id === appUserProject.roleId
                );
            }
            return undefined;
        }

        const consumedProjectIds = new Set<string>(currentProject.consumesFromProjectIds ?? []);
        const extractProjectIdsFromSections = (sections: any[] | undefined): void => {
            (sections ?? []).forEach((section: any) => {
                (section?.Columns ?? []).forEach((column: any) => {
                    (column?.Fields ?? []).forEach((field: any) => {
                        if (field?.projectId) {
                            consumedProjectIds.add(field.projectId);
                        }
                    });
                });
                extractProjectIdsFromSections(section?.Sections);
            });
        };
        extractProjectIdsFromSections(currentProject.JSONForms?.Sections);
        extractProjectIdsFromSections(currentProject.layoutDraft?.Sections);

        const activeProjectIds = new Set(
            (projects ?? [])
                .filter((project: Project): boolean => Boolean(project.active && project.id))
                .map((project: Project): string => project.id!)
        );

        const userProjectIds = new Set(
            (appUser.projects ?? [])
                .filter(userProject => Boolean(userProject.roleId || userProject.isProjectAdmin))
                .map(userProject => userProject.projectId ?? (userProject as any).id)
        );

        const hasAccessToConsumedProject = Array.from(consumedProjectIds).some(id =>
            activeProjectIds.has(id) && userProjectIds.has(id)
        );
        const summaryUserProject = appUser.projects?.find(userProject =>
            userProject.projectId === currentProject.id || (userProject as any).id === currentProject.id
        );
        const hasDirectRole = Boolean(summaryUserProject?.roleId);

        if (!hasAccessToConsumedProject && !hasDirectRole) {
            return undefined;
        }

        const mergedRules: Record<string, unknown> = {};
        const fieldToProjectIdMap = new Map<string, string>();

        (projects ?? []).forEach((project: Project) => {
            if (project.id && !project.isSummaryProject && project.JSONForms?.Sections) {
                const collectIds = (sections: any[] | undefined): void => {
                    (sections ?? []).forEach((section: any) => {
                        (section?.Columns ?? []).forEach((column: any) => {
                            (column?.Fields ?? []).forEach((field: any) => {
                                if (field?.id) {
                                    fieldToProjectIdMap.set(field.id, project.id!);
                                }
                            });
                        });
                        collectIds(section?.Sections);
                    });
                };
                collectIds(project.JSONForms.Sections);
            }
        });

        allRoles.forEach((role: Role) => {
            if (role.projectId) {
                if (role.rules) {
                    Object.keys(role.rules).forEach((fieldId: string) => {
                        fieldToProjectIdMap.set(fieldId, role.projectId!);
                    });
                }
                if (role.fields?.length) {
                    role.fields.forEach((field: any) => {
                        if (field?.id) {
                            fieldToProjectIdMap.set(field.id, role.projectId!);
                        }
                    });
                }
            }
        });

        const summaryFieldIds = new Set<string>();
        const traverseSummaryFields = (sections: any[] | undefined): void => {
            (sections ?? []).forEach((section: any) => {
                (section?.Columns ?? []).forEach((column: any) => {
                    (column?.Fields ?? []).forEach((field: any) => {
                        if (field?.id) {
                            summaryFieldIds.add(field.id);
                            if (field.projectId) {
                                fieldToProjectIdMap.set(field.id, field.projectId);
                            }
                        }
                    });
                });
                traverseSummaryFields(section?.Sections);
            });
        };
        traverseSummaryFields(currentProject.JSONForms?.Sections);
        traverseSummaryFields(currentProject.layoutDraft?.Sections);

        const hiddenRule = {
            rule: {
                effect: 'SHOW',
                condition: {
                    scope: '#',
                    schema: { not: {} }
                }
            }
        };

        summaryFieldIds.forEach((fieldId: string) => {
            const parentProjectId = fieldToProjectIdMap.get(fieldId);

            if (!parentProjectId || parentProjectId === currentProject.id) {
                if (summaryUserProject?.isProjectAdmin) {
                    mergedRules[fieldId] = {
                        rule: {
                            effect: 'ENABLE',
                            condition: {
                                scope: '#',
                                schema: {}
                            }
                        }
                    };
                }
                else if (summaryUserProject?.roleId) {
                    const directRole = allRoles.find(role =>
                        role.id === summaryUserProject.roleId?.toString() ||
                        role.id === (summaryUserProject.roleId as any)?.id ||
                        role.id === summaryUserProject.roleId
                    );
                    let directRuleForField: any;
                    if (directRole?.fields?.length) {
                        const fieldObject = directRole.fields.find((field: any) => field.id === fieldId);
                        if (fieldObject) {
                            directRuleForField = jsonFormsService.getFieldRule(fieldObject);
                        }
                    }

                    if (!directRuleForField && directRole?.rules?.[fieldId]) {
                        const raw = directRole.rules[fieldId];
                        directRuleForField = raw.rule ? cloneDeep(raw) : { rule: cloneDeep(raw) };
                    }

                    mergedRules[fieldId] = directRuleForField ?? cloneDeep(hiddenRule);
                }
                else {
                    mergedRules[fieldId] = cloneDeep(hiddenRule);
                }
                return;
            }

            if (!activeProjectIds.has(parentProjectId)) {
                mergedRules[fieldId] = cloneDeep(hiddenRule);
                return;
            }

            const parentUserProject = appUser.projects?.find(userProject =>
                userProject.projectId === parentProjectId || (userProject as any).id === parentProjectId
            );

            if (parentUserProject?.isProjectAdmin) {
                mergedRules[fieldId] = {
                    rule: {
                        effect: 'ENABLE',
                        condition: {
                            scope: '#',
                            schema: {}
                        }
                    }
                };
            }
            else if (parentUserProject?.roleId) {
                const parentRole = allRoles.find(role =>
                    role.id === parentUserProject.roleId?.toString() ||
                    role.id === (parentUserProject.roleId as any)?.id ||
                    role.id === parentUserProject.roleId
                );

                let ruleForField: any;
                if (parentRole?.fields?.length) {
                    const fieldObject = parentRole.fields.find((field: any) => field.id === fieldId);
                    if (fieldObject) {
                        ruleForField = jsonFormsService.getFieldRule(fieldObject);
                    }
                }

                if (!ruleForField && parentRole?.rules?.[fieldId]) {
                    const raw = parentRole.rules[fieldId];
                    ruleForField = raw.rule ? cloneDeep(raw) : { rule: cloneDeep(raw) };
                }

                mergedRules[fieldId] = ruleForField ?? cloneDeep(hiddenRule);
            }
            else {
                mergedRules[fieldId] = cloneDeep(hiddenRule);
            }
        });

        const combinedRole = new Role();
        combinedRole.id = `summary-role-${currentProject.id}`;
        combinedRole.name = 'Summary Combined Role';
        combinedRole.rules = mergedRules;
        return combinedRole;
    }

    addMapFieldPointGridToFields(fields: Field[]): Field[] {
        if (fields.find(x => x.id === 'map_point')) {
            return;
        }

        const MapFieldPointSection: Field = {
            id: 'map_field_point_section',
            Name: 'Map Field Point',
            type: 'Section',
            isGroupBy: true,
            showPermissions: false,
            show: true,
            enable: false,
            disableShow: true
        };

        const MapFieldPointGrid: Field = {
            id: 'map_field_point_grid',
            parentGroup: 'map_field_point_section',
            Name: 'Map Field Point',
            showPermissions: false,
            disableShow: true,
            show: true,
            FieldType: {
                id: '8',
                grid: true,
                text: 'Grid',
                type: 'Grid'
            },

        };

        if (!fields || fields.length == 0) {
            fields = [MapFieldPointSection, MapFieldPointGrid];
            return fields;
        }

        fields = [MapFieldPointSection, MapFieldPointGrid, ...fields];
        return fields;
    }
}
